/**
 * Утилиты для расчета метрик себестоимости
 */

import { isSelectionTotal, matchesSelection } from "../../../utils/restaurant";

/**
 * Рассчитывает агрегированные метрики себестоимости из данных
 * Использует summary из iiko для корректных итогов, если доступен
 */
export const calculateCostMetrics = (costData, selectedGroup = "Все") => {
  if (!costData || typeof costData !== "object") return getEmptyCostMetrics();

  const data = Array.isArray(costData.data) ? costData.data : [];
  const summary = costData?.summary;
  
  if (data.length === 0) return getEmptyCostMetrics();

  // === 1️⃣ Пытаемся получить итоги из summary (как в iiko) ===
  let summaryRow = null;
  
  if (selectedGroup && selectedGroup !== "Все") {
    // Фильтр по группе активен - ищем агрегат по группе
    summaryRow = getGroupSummaryRow(summary, selectedGroup);
  } else {
    // Фильтра по группе нет - используем общий итог
    summaryRow = getOverallSummaryRow(summary);
  }

  // === 2️⃣ Если есть итоги из summary, используем их ===
  if (summaryRow) {
    const sSales = Number(summaryRow?.DishDiscountSumInt || 0);
    const sCost = Number(
      summaryRow?.["ProductCostBase.ProductCost"] ||
      summaryRow?.ProductCostBase?.ProductCost ||
      0
    );
    const sProfit = Number(
      summaryRow?.["ProductCostBase.Profit"] ||
      summaryRow?.ProductCostBase?.Profit ||
      0
    );
    const sDiscount = Number(summaryRow?.DiscountSum || 0);

    // Проценты — из сумм, а не из ProductCostBase.Percent / MarkUp iiko: iiko отдаёт их долями (наценка 381 % — 3,81),
    // и прежняя эвристика «> 1 — уже проценты» показывала наценку 3,81 % вместо 381 %
    const costPercent = sSales > 0 ? (sCost / sSales) * 100 : 0;
    // Наценка % как в iiko: прибыль / себестоимость × 100
    const markupPercent = sCost > 0 ? (sProfit / sCost) * 100 : 0;

    return {
      totalSales: sSales,
      totalCost: sCost,
      totalProfit: sProfit,
      totalMarkup: sProfit, // Наценка в рублях = прибыль
      totalDiscount: sDiscount,
      costPercent,
      markupPercent,
      avgCostPercent: costPercent,
      avgMarkupPercent: markupPercent,
    };
  }

  // === 3️⃣ Если summary нет, считаем из data (как раньше) ===
  const filtered = data.filter((d) => {
    return matchesSelection(d, selectedGroup);
  });

  // Выбор без строк — нули (страница показывает «нет продаж»), а не цифры всей сети, как раньше
  const useData = filtered;

  // Агрегируем метрики
  let totalSales = 0; // Сумма со скидкой (DishDiscountSumInt)
  let totalCost = 0; // Себестоимость (ProductCostBase.ProductCost)
  let totalProfit = 0; // Валовая прибыль (ProductCostBase.Profit)
  let totalMarkup = 0; // Наценка в рублях (ProductCostBase.Profit)
  let totalDiscount = 0; // Сумма скидки (DiscountSum)

  useData.forEach((d) => {
    const sales = Number(d?.DishDiscountSumInt || 0);
    // Поля могут приходить с точкой в ключе или как вложенные объекты
    const cost = Number(
      d?.["ProductCostBase.ProductCost"] ||
      d?.ProductCostBase?.ProductCost ||
      0
    );
    const profit = Number(
      d?.["ProductCostBase.Profit"] ||
      d?.ProductCostBase?.Profit ||
      0
    );
    const discount = Number(d?.DiscountSum || 0);

    totalSales += sales;
    totalCost += cost;
    totalProfit += profit;
    totalMarkup += profit; // Наценка в рублях = прибыль
    totalDiscount += discount; // Сумма скидки
  });

  // Вычисляем процент себестоимости от суммы продаж
  const costPercentFromSales = totalSales > 0 ? (totalCost / totalSales) * 100 : 0;
  // Наценка % в iiko рассчитывается как (Наценка / Себестоимость) * 100, а не от продаж
  const markupPercentFromCost = totalCost > 0 ? (totalMarkup / totalCost) * 100 : 0;

  return {
    totalSales, // Сумма со скидкой (₽)
    totalCost, // Себестоимость продаж (₽)
    totalProfit, // Валовая прибыль (₽)
    totalMarkup, // Наценка (₽)
    totalDiscount, // Сумма скидки (₽)
    costPercent: costPercentFromSales, // Себестоимость (% от продаж)
    markupPercent: markupPercentFromCost, // Наценка (% от себестоимости, как в iiko)
    avgCostPercent: costPercentFromSales,
    avgMarkupPercent: markupPercentFromCost,
  };
};

const num = (value) => Number(value || 0);

// Есть ли у выбранного ресторана (группы) продажи за период: нет — страница показывает «нет продаж» вместо карточек
export const hasSalesForSelection = (costData, selectedGroup = "Все") =>
  Array.isArray(costData?.data) && costData.data.some((d) => matchesSelection(d, selectedGroup));

// Не блюда меню — в топы не попадают (тот же список, что у «Кухни» и ABC-анализа). В итогах карточек они есть
const NOT_DISHES = ["MODIFIER", "SERVICE"];

/**
 * Строки выбранного ресторана (группы), сложенные по блюду: одно блюдо приходит строкой на каждую
 * группу и ресторан, а в топе должно быть один раз. Модификаторы и услуги пропускаются. Процент себестоимости —
 * себестоимость / выручка по сумме строк
 */
function dishesForSelection(costData, selectedGroup) {
  if (!costData || typeof costData !== "object") return [];
  const data = Array.isArray(costData.data) ? costData.data : [];

  const dishes = new Map();
  for (const d of data) {
    if (!matchesSelection(d, selectedGroup)) continue;
    if (NOT_DISHES.includes(d.DishType)) continue;
    const dishName = d.DishName || "Неизвестное блюдо";
    const dish = dishes.get(dishName) || { dishName, profit: 0, ordersCount: 0, sales: 0, cost: 0 };
    dish.profit += num(d?.["ProductCostBase.Profit"] ?? d?.ProductCostBase?.Profit);
    dish.ordersCount += num(d?.["UniqOrderId.OrdersCount"] ?? d?.UniqOrderId?.OrdersCount);
    dish.sales += num(d?.DishDiscountSumInt);
    dish.cost += num(d?.["ProductCostBase.ProductCost"] ?? d?.ProductCostBase?.ProductCost);
    dishes.set(dishName, dish);
  }

  // Без выручки процента нет (0) — в топ себестоимости такое блюдо не попадает
  return [...dishes.values()].map((dish) => ({
    ...dish,
    costPercent: dish.sales > 0 ? (dish.cost / dish.sales) * 100 : 0,
  }));
}

/**
 * Получает топ-5 блюд по валовой прибыли
 */
export const getTopDishesByProfit = (costData, selectedGroup = "Все", limit = 5) =>
  dishesForSelection(costData, selectedGroup)
    .filter((d) => d.profit > 0) // Только блюда с прибылью
    .sort((a, b) => b.profit - a.profit)
    .slice(0, limit);

// Топ по проценту себестоимости — только блюда, у которых за период не меньше 5 заказов: иначе наверх выходит
// блюдо, проданное раз-другой со скидкой
export const MIN_ORDERS_FOR_COST_TOP = 5;

/**
 * Получает топ-5 блюд по проценту себестоимости
 */
export const getTopDishesByCostPercent = (costData, selectedGroup = "Все", limit = 5) =>
  dishesForSelection(costData, selectedGroup)
    .filter((d) => d.costPercent > 0 && d.ordersCount >= MIN_ORDERS_FOR_COST_TOP)
    .sort((a, b) => b.costPercent - a.costPercent)
    .slice(0, limit);

// Почему топ пуст — текст вместо «Нет данных». Продажи у выбора есть (без них страница пишет «нет продаж»)
const ONLY_NOT_DISHES = "За период проданы только модификаторы и услуги — блюд нет";

export const emptyTopProfitReason = (costData, selectedGroup = "Все") =>
  dishesForSelection(costData, selectedGroup).length === 0
    ? ONLY_NOT_DISHES
    : "Ни одно блюдо не дало прибыли: себестоимость не меньше выручки (например, всё продано со скидкой 100 %)";

export const emptyTopCostReason = (costData, selectedGroup = "Все") => {
  const dishes = dishesForSelection(costData, selectedGroup);
  if (dishes.length === 0) return ONLY_NOT_DISHES;
  const frequent = dishes.filter((d) => d.ordersCount >= MIN_ORDERS_FOR_COST_TOP);
  if (frequent.length === 0) {
    return `Ни одно блюдо не заказали ${MIN_ORDERS_FOR_COST_TOP} и более раз — выберите период длиннее`;
  }
  if (frequent.every((d) => d.sales <= 0)) return `Блюда с ${MIN_ORDERS_FOR_COST_TOP} и более заказами проданы без выручки (скидка 100 %)`;
  if (frequent.every((d) => d.sales <= 0 || d.cost <= 0)) {
    return `У блюд с ${MIN_ORDERS_FOR_COST_TOP} и более заказами нет себестоимости в iiko — проверьте техкарты`;
  }
  return "Нет данных";
};

/**
 * Извлекает общий итог из summary (пустой groupObj {})
 */
function getOverallSummaryRow(summary) {
  if (!Array.isArray(summary)) return null;
  for (const row of summary) {
    if (Array.isArray(row) && row.length === 2) {
      const groupObj = row[0] || {};
      const metricsObj = row[1] || null;
      if (groupObj && typeof groupObj === "object" && Object.keys(groupObj).length === 0) {
        return metricsObj;
      }
    } else if (row && typeof row === "object") {
      // На всякий случай поддержим плоский вариант summary
      return row;
    }
  }
  return null;
}

/**
 * Извлекает агрегат по выбранному ресторану (или группе в нём) из summary:
 * строка ровно этого уровня, без разбивки по блюдам (см. isSelectionTotal)
 */
function getGroupSummaryRow(summary, selectedGroup) {
  if (!Array.isArray(summary) || !selectedGroup) return null;

  for (const row of summary) {
    if (Array.isArray(row) && row.length === 2 && isSelectionTotal(row[0], selectedGroup) && row[1]) {
      return row[1];
    }
  }
  // Итога этого уровня нет — считаем по строкам данных (а не по итогу одного блюда)
  return null;
}

/**
 * Заглушка для пустых метрик
 */
function getEmptyCostMetrics() {
  return {
    totalSales: 0,
    totalCost: 0,
    totalProfit: 0,
    totalMarkup: 0,
    totalDiscount: 0,
    costPercent: 0,
    markupPercent: 0,
    avgCostPercent: 0,
    avgMarkupPercent: 0,
  };
}

